// =============================================================================
// SIREPPAR - Tablero de estado de la documentación del expediente
// =============================================================================
// Reemplaza al panel "Para avanzar de etapa" en la tab Documentación. Ese panel
// contaba pendientes; este contesta, DOCUMENTO POR DOCUMENTO, qué pasó con cada
// uno — que es la pregunta real cuando el trámite ya está en análisis y hubo
// varias idas y vueltas con el partido.
//
// Cada FILA es un TIPO de documento, no un archivo: el partido puede haber
// presentado el mismo tipo tres veces. Cada intento tiene su símbolo en la
// columna "Archivos", del más viejo al más nuevo, y al pulsarlo se lee su motivo
// en la columna de al lado.
//
// NO entran acá los DNI (tienen su propia sección en /documentos, una fila por
// persona y por lado) ni la Documentación TEP (son los PDF que genera el propio
// circuito, no documentación que el partido deba presentar).
//
// El detalle sigue viviendo en /documentos: el nombre de cada fila es un link al
// acordeón de ese tipo, ya abierto, y desde allá se vuelve a esta misma fila.
// =============================================================================

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { documentoService, partidoService } from '../../../services/api';
import CuadroNota from '../../../components/common/CuadroNota';
import {
  tipoDocLabels,
  TIPOS_DNI_PERSONA,
  TIPOS_RESOLUCIONES,
} from '../../../utils/tiposDocumento';

// =============================================================================
// Tipos
// =============================================================================

type EstadoRevision = 'PENDIENTE' | 'APROBADO' | 'RECHAZADO';
type EstadoTipo = 'verificado' | 'rechazado' | 'pendiente' | 'faltante';

export interface DocTablero {
  id: string;
  tipo: string;
  titulo?: string;
  nombreOriginal?: string;
  creadoEn: string;
  estadoRevision: EstadoRevision;
  observacion?: string | null;
  revisadoPorNombre?: string | null;
  revisadoEn?: string | null;
  nota?: string | null;
  observacionRespuestaId?: string | null;
  intimacionRespuestaId?: string | null;
  virtual?: boolean;
}

export interface ObservacionTablero {
  id: string;
  estado: string;
  subsanada: boolean;
  creadoEn: string;
  fechaLimite?: string;
}

export interface ResumenAfiliados {
  total: number;
  afiliadosValidos: number;
  minimoRequerido: number;
}

interface Props {
  expedienteId: string;
  partidoId?: string;
  partidoNota?: string | null;
  docs: DocTablero[];
  tiposObligatorios: Array<{ tipo: string; label: string }>;
  observaciones: ObservacionTablero[];
  afiliados?: ResumenAfiliados | null;
  puedeEditar: boolean;
}

// =============================================================================
// Helpers
// =============================================================================

// Grilla compartida por el encabezado y todas las filas. Si cambia una, cambia
// la otra: son la misma tabla.
const GRID = 'grid grid-cols-[minmax(0,17rem)_8rem_7rem_9.5rem_minmax(0,1fr)_15rem] gap-3';

function fmtFecha(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '' : d.toLocaleDateString('es-AR');
}

// Misma regla de consolidación que /documentos (getEstadoTipo): un pendiente
// manda sobre todo, después alcanza un aprobado para dar el tipo por cumplido, y
// si todos fueron rechazados el tipo está rechazado.
function estadoDelTipo(archivos: DocTablero[]): EstadoTipo {
  if (archivos.length === 0) return 'faltante';
  if (archivos.some(a => a.estadoRevision === 'PENDIENTE')) return 'pendiente';
  if (archivos.some(a => a.estadoRevision === 'APROBADO')) return 'verificado';
  return 'rechazado';
}

const BADGE_ESTADO: Record<EstadoTipo, { label: string; cls: string }> = {
  verificado: { label: 'Verificado', cls: 'bg-green-100 text-green-700' },
  rechazado: { label: 'Rechazado', cls: 'bg-red-100 text-red-700' },
  pendiente: { label: 'Pendiente', cls: 'bg-amber-50 text-amber-700' },
  faltante: { label: 'Sin cargar', cls: 'bg-gray-100 text-gray-600' },
};

// Un símbolo por archivo presentado. El color es el estado de ESE archivo.
function SimboloArchivo({ estado, activo, titulo, onClick }: {
  estado: EstadoRevision;
  activo: boolean;
  titulo: string;
  onClick: () => void;
}) {
  const fondo = estado === 'APROBADO' ? 'bg-green-600'
    : estado === 'RECHAZADO' ? 'bg-red-600' : 'bg-amber-500';
  const anillo = estado === 'APROBADO' ? 'ring-green-300'
    : estado === 'RECHAZADO' ? 'ring-red-300' : 'ring-amber-300';
  return (
    <button
      type="button"
      onClick={onClick}
      title={titulo}
      aria-label={titulo}
      className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 transition-shadow
        ${fondo} ${activo ? `ring-2 ring-offset-1 ${anillo}` : ''}`}
    >
      {estado === 'APROBADO' && (
        <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 12 12" stroke="currentColor" strokeWidth={2.2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M2.5 6L5 8.5L9.5 3.5" />
        </svg>
      )}
      {estado === 'RECHAZADO' && (
        <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 12 12" stroke="currentColor" strokeWidth={2.2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M3 3L9 9M9 3L3 9" />
        </svg>
      )}
      {estado === 'PENDIENTE' && <span className="w-2 h-2 rounded-full bg-white/90" />}
    </button>
  );
}

// =============================================================================
// Celda de nota — se guarda al salir del campo, no en cada tecla
// =============================================================================

function NotaCelda({ docId, valor, puedeEditar, onGuardado }: {
  docId: string;
  valor: string;
  puedeEditar: boolean;
  onGuardado: () => void;
}) {
  // El cuadro vive en components/common/CuadroNota: es el mismo que usan las
  // secciones de alianza, y tenerlo en un solo lugar evita que se vayan separando.
  return (
    <CuadroNota
      valor={valor}
      puedeEditar={puedeEditar}
      dataAttr={{ 'data-nota-doc': docId }}
      onGuardar={async (nota) => {
        try {
          await documentoService.updateNota(docId, nota);
          toast.success('Nota guardada');
          onGuardado();
        } catch (err: any) {
          toast.error(err?.response?.data?.error?.message || 'No se pudo guardar la nota');
        }
      }}
    />
  );
}

// =============================================================================
// Nota general del partido
// =============================================================================

function NotaPartido({ partidoId, expedienteId, valor, puedeEditar }: { partidoId: string; expedienteId: string; valor: string; puedeEditar: boolean }) {
  const queryClient = useQueryClient();
  const [texto, setTexto] = useState(valor);

  useEffect(() => { setTexto(valor); }, [valor]);

  const sucio = texto.trim() !== valor.trim();

  const mutation = useMutation({
    mutationFn: (nota: string) => partidoService.updateNota(partidoId, nota),
    onSuccess: () => {
      toast.success('Nota guardada');
      // La nota llega dentro del expediente (expediente.partido.nota) y el listado
      // de partidos muestra la MISMA columna, así que hay que invalidar las dos
      // caches: con staleTime de 5 minutos, sin esto la otra pantalla sigue
      // mostrando la nota anterior.
      queryClient.invalidateQueries({ queryKey: ['expediente', expedienteId] });
      queryClient.invalidateQueries({ queryKey: ['partidos-cumplimiento'] });
      queryClient.invalidateQueries({ queryKey: ['partidos'] });
    },
    onError: (err: any) =>
      toast.error(err?.response?.data?.error?.message || 'No se pudo guardar la nota'),
  });

  return (
    <div className="bg-yellow-50 border border-gray-400 rounded-lg px-5 py-4">
      <div className="flex items-baseline justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Nota general del partido</h2>
        </div>
      </div>
      {/* MESA_ENTRADAS y demás roles sin escritura la ven, no la editan: el
          backend rechaza el PATCH y no tiene sentido ofrecer un campo que falla. */}
      {puedeEditar ? (
        <div className="relative mt-3 min-w-0">
          <textarea
            value={texto}
            onChange={e => setTexto(e.target.value)}
            placeholder="Nota general del partido…"
            rows={3}
            maxLength={2000}
            className="block w-full resize-y border border-gray-400 rounded-md pl-3 pr-3 pt-2 pb-9 text-sm
              leading-relaxed bg-yellow-50 placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
          />
          <button
            onClick={() => mutation.mutate(texto)}
            disabled={!sucio || mutation.isPending}
            title={sucio ? 'Guardar la nota' : 'No hay cambios para guardar'}
            className="absolute bottom-2 right-2 border border-black text-black bg-white text-xs leading-none
              px-2.5 py-1.5 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
          >
            {mutation.isPending ? 'Guardando…' : 'Anotar'}
          </button>
        </div>
      ) : texto ? (
        <p className="mt-3 bg-yellow-50 border border-gray-400 rounded-md px-3 py-2 text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">
          {texto}
        </p>
      ) : (
        <p className="mt-3 text-sm text-gray-500">Sin nota.</p>
      )}
    </div>
  );
}

// =============================================================================
// Tablero
// =============================================================================

export default function TableroDocumentacion({
  expedienteId, partidoId, partidoNota, docs, tiposObligatorios,
  observaciones, afiliados, puedeEditar,
}: Props) {
  const queryClient = useQueryClient();
  // Archivo cuyo motivo está abierto: "<tipo>:<índice>". Uno solo a la vez.
  const [seleccion, setSeleccion] = useState<string | null>(null);

  const refrescar = () => queryClient.invalidateQueries({ queryKey: ['documentos-checklist', expedienteId] });

  const reales = docs.filter(d => !d.virtual);
  const porTipo = (tipo: string) => reales
    .filter(d => d.tipo === tipo)
    .sort((a, b) => new Date(a.creadoEn).getTime() - new Date(b.creadoEn).getTime());

  // --- Grupos -----------------------------------------------------------------
  const tiposObligatoriosSet = new Set(tiposObligatorios.map(t => t.tipo));
  const excluidos = new Set([
    ...TIPOS_DNI_PERSONA, 'DNI_FRENTE_INICIADOR', 'DNI_DORSO_INICIADOR', 'DNI_AUTORIDAD', 'DNI_APODERADO',
    ...TIPOS_RESOLUCIONES,
    'SIMBOLO',
  ]);

  const tiposOtros = Array.from(new Set(
    reales.filter(d => !tiposObligatoriosSet.has(d.tipo) && !excluidos.has(d.tipo)).map(d => d.tipo),
  )).sort((a, b) => (tipoDocLabels[a] || a).localeCompare(tipoDocLabels[b] || b, 'es'));

  const haySimbolos = reales.some(d => d.tipo === 'SIMBOLO');

  // --- Estado de observación --------------------------------------------------
  // Las observaciones son del EXPEDIENTE, no del documento: no hay forma de saber
  // por cuál documento se cursó. Lo que sí se sabe por documento es si entró como
  // RESPUESTA a una observación (observacionRespuestaId / intimacionRespuestaId).
  // De ahí las dos únicas afirmaciones honestas por fila:
  //   - "Respondió": algún archivo del tipo llegó adjunto a una respuesta.
  //   - "Observado": el tipo está rechazado o sin cargar y hay una observación
  //     abierta, así que es de lo que se le está reclamando.
  const obsAbierta = observaciones.find(o => o.estado === 'PENDIENTE' && !o.subsanada);

  const grupos: Array<{ titulo: string; tipos: Array<{ tipo: string; label: string }> }> = [
    { titulo: 'Documentos obligatorios', tipos: tiposObligatorios },
    { titulo: 'Otros documentos', tipos: tiposOtros.map(t => ({ tipo: t, label: tipoDocLabels[t] || t })) },
    ...(haySimbolos
      ? [{ titulo: 'Emblemas y símbolos', tipos: [{ tipo: 'SIMBOLO', label: tipoDocLabels.SIMBOLO }] }]
      : []),
  ];

  const filasTotales = grupos.reduce((n, g) => n + g.tipos.length, 0);

  return (
    <div className="space-y-4">
      {partidoId && <NotaPartido partidoId={partidoId} expedienteId={expedienteId} valor={partidoNota || ''} puedeEditar={puedeEditar} />}

      <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
        {/* Encabezado */}
        <div className="px-5 py-4 border-b border-gray-200">
          <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Estado de la documentación</h2>
          <p className="text-xs text-gray-500 mt-1">
            No entran acá los DNI ni la documentación del TEP.
          </p>
        </div>

        {/* Títulos de columna */}
        <div className={`${GRID} px-5 py-2.5 bg-gray-50 border-b border-gray-400`}>
          {['Documento', 'Archivos', 'Estado', 'Observación', 'Motivo', 'Nota de la Secretaría'].map(t => (
            <span key={t} className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">{t}</span>
          ))}
        </div>

        {filasTotales === 0 && (
          <p className="px-5 py-6 text-sm text-gray-400 text-center">
            Este expediente todavía no tiene documentación para listar.
          </p>
        )}

        {grupos.map(grupo => grupo.tipos.length === 0 ? null : (
          <div key={grupo.titulo}>
            <div className="flex items-center gap-2.5 px-5 py-2 bg-gray-100 border-b border-gray-400">
              <span className="text-[11px] font-bold text-gray-600 uppercase tracking-widest">{grupo.titulo}</span>
              <span className="text-[11px] text-gray-400">
                {grupo.tipos.length} {grupo.tipos.length === 1 ? 'tipo' : 'tipos'}
              </span>
            </div>

            {grupo.tipos.map(({ tipo, label }) => {
              const archivos = porTipo(tipo);
              const estado = estadoDelTipo(archivos);
              const ultimo = archivos[archivos.length - 1];
              const badge = BADGE_ESTADO[estado];

              // Archivo seleccionado de ESTA fila (si hay alguno).
              const idxSel = seleccion?.startsWith(`${tipo}:`)
                ? parseInt(seleccion.slice(tipo.length + 1), 10)
                : -1;
              const archivoSel = idxSel >= 0 ? archivos[idxSel] : undefined;

              // La fecha sigue al archivo elegido; sin elección, la del más reciente.
              const fechaCelda = archivoSel?.creadoEn ?? ultimo?.creadoEn;

              const respondio = archivos.find(a => a.observacionRespuestaId || a.intimacionRespuestaId);
              const observado = !respondio && !!obsAbierta && (estado === 'rechazado' || estado === 'faltante');

              return (
                <div
                  key={tipo}
                  id={`doc-${tipo}`}
                  className={`${GRID} px-5 py-3.5 border-b border-gray-400 items-start scroll-mt-28 ${
                    estado === 'rechazado' ? 'bg-red-50/40' : ''
                  }`}
                >
                  {/* Documento — link al acordeón de /documentos */}
                  <div className="min-w-0">
                    {/* Sin subtítulo de conteo: cuántos archivos hay ya lo dicen los
                        símbolos de la columna de al lado, uno por archivo. */}
                    <Link
                      to={`/documentos?expedienteId=${expedienteId}${partidoId ? `&partidoId=${partidoId}` : ''}&abrirTipo=${tipo}`}
                      className="text-[15px] font-medium text-primary-600 hover:text-primary-700 hover:underline leading-snug inline-flex items-start gap-1.5"
                    >
                      <span>{label}</span>
                      <svg className="w-3.5 h-3.5 flex-shrink-0 mt-1 opacity-60" fill="none" viewBox="0 0 16 16" stroke="currentColor" strokeWidth={1.8}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6.5 3.5H3.5v9h9V9.5M9.5 2.5h4v4M13.5 2.5 7.5 8.5" />
                      </svg>
                    </Link>
                  </div>

                  {/* Archivos — un símbolo por archivo */}
                  <div className="flex flex-col gap-1.5 items-start">
                    <div className="flex flex-wrap gap-1.5">
                      {archivos.length === 0
                        ? <span className="w-6 h-6 rounded-full border-2 border-dashed border-gray-300" />
                        : archivos.map((a, i) => (
                          <SimboloArchivo
                            key={a.id}
                            estado={a.estadoRevision}
                            activo={idxSel === i}
                            titulo={`Archivo ${i + 1} de ${archivos.length} · ${fmtFecha(a.creadoEn)}`}
                            onClick={() => setSeleccion(idxSel === i ? null : `${tipo}:${i}`)}
                          />
                        ))}
                    </div>
                    <span className="text-[10px] text-gray-400 leading-tight">
                      {fechaCelda ? fmtFecha(fechaCelda) : 'nunca presentado'}
                    </span>
                  </div>

                  {/* Estado consolidado del tipo */}
                  <div>
                    <span className={`text-[11px] font-medium px-2 py-1 rounded-full whitespace-nowrap ${badge.cls}`}>
                      {badge.label}
                    </span>
                  </div>

                  {/* Observación / intimación */}
                  <div className="min-w-0">
                    {respondio ? (
                      <>
                        <span className="text-[11px] font-medium px-2 py-1 rounded-full whitespace-nowrap bg-blue-100 text-blue-700">
                          Respondió
                        </span>
                        <p className="text-[10px] text-gray-400 mt-1 leading-tight">
                          Adjuntó {fmtFecha(respondio.creadoEn)}
                          {estado === 'pendiente' && ' · falta verificar'}
                        </p>
                      </>
                    ) : observado ? (
                      <>
                        <span className="text-[11px] font-medium px-2 py-1 rounded-full whitespace-nowrap bg-orange-100 text-orange-800">
                          Observado
                        </span>
                        <p className="text-[10px] text-gray-400 mt-1 leading-tight">
                          {obsAbierta?.fechaLimite
                            ? `Vence ${fmtFecha(obsAbierta.fechaLimite)}`
                            : `Cursada ${fmtFecha(obsAbierta?.creadoEn)}`}
                        </p>
                      </>
                    ) : (
                      <span className="text-xs text-gray-300">—</span>
                    )}
                  </div>

                  {/* Motivo del archivo elegido */}
                  <div className="min-w-0">
                    {archivoSel ? (
                      <div className={`border rounded-md px-2.5 py-2 ${
                        archivoSel.estadoRevision === 'RECHAZADO' ? 'border-red-200 bg-red-50'
                          : archivoSel.estadoRevision === 'APROBADO' ? 'border-green-200 bg-green-50'
                          : 'border-amber-200 bg-amber-50'
                      }`}>
                        <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">
                          Archivo {idxSel + 1} de {archivos.length} ·{' '}
                          {archivoSel.estadoRevision === 'APROBADO' ? 'verificado'
                            : archivoSel.estadoRevision === 'RECHAZADO' ? 'rechazado' : 'pendiente'}
                        </p>
                        <p className="text-xs text-gray-700 mt-1 leading-relaxed whitespace-pre-wrap">
                          {archivoSel.observacion
                            || (archivoSel.estadoRevision === 'PENDIENTE'
                              ? `Presentado el ${fmtFecha(archivoSel.creadoEn)}. Todavía nadie lo verificó.`
                              : 'Sin motivo registrado.')}
                        </p>
                        {archivoSel.revisadoPorNombre && (
                          <p className="text-[10px] text-gray-400 mt-1">
                            {archivoSel.revisadoPorNombre}
                            {archivoSel.revisadoEn && ` · ${fmtFecha(archivoSel.revisadoEn)}`}
                          </p>
                        )}
                      </div>
                    ) : (
                      <p className="text-[11px] text-gray-300 leading-snug">
                        {archivos.length === 0 ? 'No hay archivos que mostrar.' : 'Pulsá un símbolo.'}
                      </p>
                    )}
                  </div>

                  {/* Nota de la Secretaría — cuelga del archivo más reciente del tipo */}
                  <div>
                    {ultimo ? (
                      <NotaCelda
                        key={ultimo.id}
                        docId={ultimo.id}
                        valor={ultimo.nota || ''}
                        puedeEditar={puedeEditar}
                        onGuardado={refrescar}
                      />
                    ) : (
                      <p className="text-[11px] text-gray-300 leading-snug">
                        Sin archivo. Lo que haya que anotar va en la nota general del partido.
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ))}

        {/* Renglón fijo de afiliación: el 4‰ siempre a la vista, aunque la
            documentación esté completa. */}
        <div className="grid grid-cols-[minmax(0,17rem)_minmax(0,1fr)] gap-3 px-5 py-4 bg-primary-50 border-t-2 border-primary-200 items-center">
          <div>
            <p className="text-[11px] font-bold text-primary-800 uppercase tracking-widest">Afiliación</p>
            {partidoId && (
              <Link to={`/afiliados?partido=${partidoId}`} className="text-[13px] font-medium text-primary-600 hover:text-primary-700 hover:underline">
                Padrón de afiliados →
              </Link>
            )}
          </div>
          {afiliados ? (() => {
            const cumple = afiliados.afiliadosValidos >= afiliados.minimoRequerido;
            const pct = afiliados.minimoRequerido > 0
              ? Math.min(100, (afiliados.afiliadosValidos / afiliados.minimoRequerido) * 100)
              : 100;
            const faltan = Math.max(0, afiliados.minimoRequerido - afiliados.afiliadosValidos);
            return (
              <div>
                <div className="flex items-baseline gap-2 flex-wrap">
                  <span className={`text-2xl font-bold leading-none ${cumple ? 'text-green-700' : 'text-red-700'}`}>
                    {afiliados.afiliadosValidos.toLocaleString('es-AR')}
                  </span>
                  <span className="text-lg text-gray-400 leading-none">/</span>
                  <span className="text-lg font-bold text-gray-700 leading-none">
                    {afiliados.minimoRequerido.toLocaleString('es-AR')}
                  </span>
                  <span className="text-xs text-gray-600">válidos sobre el mínimo del 4‰</span>
                  {cumple ? (
                    <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-green-100 text-green-700">cumple</span>
                  ) : (
                    <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-red-100 text-red-700">
                      faltan {faltan.toLocaleString('es-AR')}
                    </span>
                  )}
                </div>
                <div className="mt-2 h-1.5 rounded-full bg-primary-100 overflow-hidden">
                  <div
                    className={`h-full rounded-full ${cumple ? 'bg-green-600' : 'bg-red-600'}`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <p className="text-[11px] text-gray-500 mt-1.5">
                  {afiliados.total.toLocaleString('es-AR')} fichas presentadas ·{' '}
                  {(afiliados.total - afiliados.afiliadosValidos).toLocaleString('es-AR')} observadas en el cotejo
                </p>
              </div>
            );
          })() : (
            <p className="text-xs text-gray-400">Cargando el resumen de afiliación…</p>
          )}
        </div>
      </div>
    </div>
  );
}
